import * as React from "react";
import * as ReactDOM from "react-dom";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { Sounds } from "source/app/services/Sound/Sounds";
import swal, { SweetAlertOptions } from "sweetalert2";
import { Or } from "../../../domain/Or";
import { BannerImages, IBannerImage } from "./BannerImage/BannerImage";
import * as styles from "./RenderSwal.css";
import { ShockSwalBackground } from "./ShockSwalBackground/ShockSwalBackground";
import { SwalBody } from "./SwalBody/SwalBody";
import ReactTooltip from "react-tooltip";

const RenderSwal = (
    swalOptions: TSwalOptions | null,
    Component: JSX.Element,
    // Causes the 'OK' button to display
    Fulfill?: Function,
    Cancel?: Function
) => {
    if (!swalOptions) swalOptions = {};

    // Calling this before creating any new windows
    // in case multiple windows were opened in the background
    UnmountAllSwals();

    const panelBody = `<div id='swalBody' class='${styles.defaults}'></div>`;
    const backgroundFadeInDurationInMilliseconds =
        swalOptions.backgroundFadeInDurationInMilliseconds;
    const shouldFadePopupBackground =
        backgroundFadeInDurationInMilliseconds && !IsIosDevice();
    const popupBackground = Or(
        swalOptions.background,
        "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(/images/backgrounds/0_swal.jpg)"
    );

    swalOptions = {
        html: panelBody,
        customClass: styles.defaults,
        width: Or(swalOptions.maxWidth, `330px`),
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: false,
        buttonsStyling: false,
        animation: !IsMobileDevice(),
        ...swalOptions,
        background: shouldFadePopupBackground
            ? "transparent"
            : popupBackground,
    };

    // try {
    UnmountAllSwals();
    ReactTooltip.hide();
    swal(swalOptions);
    if (shouldFadePopupBackground) {
        FadeInPopupBackground(
            popupBackground,
            backgroundFadeInDurationInMilliseconds
        );
    }
    // } finally {
    // }

    ShockSwalBackground(swalOptions);

    ReactDOM.render(
        <SwalBody options={swalOptions} Fulfill={Fulfill} Reject={Cancel}>
            {Component}
        </SwalBody>,
        document.getElementById("swalBody")
    );
};

interface IAdditionalSwalOptions {
    shockBackgroundColor?: string;
    showBorders?: boolean;
    showButtonDivider?: boolean;
    buttonDividerHeight?: string;
    buttonDividerButtonsAtBottom?: boolean;
    contentTopFade?: boolean;
    contentTopFadeHeight?: string;
    contentBottomFade?: boolean;
    contentBottomFadeHeight?: string;
    background?: string;
    backgroundFadeInDurationInMilliseconds?: number;
    bannerImage?: IBannerImage;
    height?: string;
    maxWidth?: string;
    columnCount?: number;
}

export default RenderSwal;

export type TSwalOptions = Partial<SweetAlertOptions> & IAdditionalSwalOptions;

function IsIosDevice(): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }

    const platform = navigator.platform || "";
    const userAgent = navigator.userAgent || "";

    return (
        /iPad|iPhone|iPod/.test(userAgent) ||
        (platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
}

function FadeInPopupBackground(background: string, durationInMilliseconds: number) {
    const popup = document.querySelector<HTMLElement>(".swal2-popup");

    if (!popup) {
        return;
    }

    const backgroundLayer = document.createElement("div");
    backgroundLayer.style.position = "absolute";
    backgroundLayer.style.inset = "0";
    backgroundLayer.style.zIndex = "-1";
    backgroundLayer.style.borderRadius = "inherit";
    backgroundLayer.style.pointerEvents = "none";
    backgroundLayer.style.background = background;
    backgroundLayer.style.opacity = "0";
    backgroundLayer.style.transition = `opacity ${durationInMilliseconds}ms ease-in-out`;

    popup.style.position = "relative";
    popup.style.zIndex = "0";
    popup.style.isolation = "isolate";
    popup.style.overflow = "hidden";
    popup.insertBefore(backgroundLayer, popup.firstChild);

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            backgroundLayer.style.opacity = "1";
        });
    });
}

export const UnmountAllSwals = () => {
    const swalBody = document.getElementById("swalBody");

    if (swalBody) {
        ReactDOM.unmountComponentAtNode(swalBody);
    }

    RemoveSwalFromDOM();
};

const RemoveSwalFromDOM = () => {
    var containerElement = document.querySelector(".swal2-container")!;

    if (containerElement === null) {
        return;
    }
    
    containerElement.parentNode!.removeChild(containerElement);

    // Remove unwanted swal classes
    document.querySelector("body")!.classList.remove("swal2-shown");
    document.querySelector("body")!.classList.remove("swal2-height-auto");
};

export const AreYouSure = (
    questionText: string,
    OnConfirm: Function,
    height: string = `30px`
) => {
    Sounds.grunt.Play();

    RenderSwal(
        {
            height,
            bannerImage: BannerImages.fire,
        },
        <div style={{ color: "white", paddingBottom: `10px` }}>
            {questionText}
        </div>,
        OnConfirm,
        () => {}
    );
};
